import * as Sentry from '@sentry/react';
import Keycloak from 'keycloak-js';
import React, { createContext, useContext, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router';

import LoadingIndicator from '@components/LoadingIndicator/LoadingIndicator';
import { APP_SETTINGS, KEYCLOAK_CLIENT_ID, KEYCLOAK_URL } from '@constants/constants';
import { updateAxiosToken } from '@hooks/useAxios';
import { ROUTE_PATHS } from '@router/paths';
import { clearUser } from '@utils/openreplay';

type Ctx = { keycloak: Keycloak | null; initialized: boolean };
const KeycloakCtx = createContext<Ctx>({ keycloak: null, initialized: false });

export const useKeycloak = () => useContext(KeycloakCtx);

type KeycloakProviderProps = {
  children: React.ReactNode;
  mock?: boolean;
};

const createMockKeycloak = (): Keycloak =>
  ({
    authenticated: true,
    token: 'mock-token',
    tokenParsed: { exp: Math.floor(Date.now() / 1000) + 3600 },
    updateToken: async () => true,
    logout: () => {},
  }) as unknown as Keycloak;

export const KeycloakProvider: React.FC<KeycloakProviderProps> = ({ children, mock }) => {
  const [keycloak, setKeycloak] = useState<Keycloak | null>(() => (mock ? createMockKeycloak() : null));
  const [initialized, setInitialized] = useState(() => Boolean(mock));
  const navigate = useNavigate();
  // Used to force a re-render when the keycloak instance is updated internally.
  // We cannot spread the keycloak instance ({...kc}) because it destroys class methods.
  const [, setTick] = useState(0);

  const refreshTimer = useRef<number>();
  const navigateRef = useRef(navigate);

  useEffect(() => {
    navigateRef.current = navigate;
  }, [navigate]);

  useEffect(() => {
    const clearTimer = () => clearTimeout(refreshTimer.current);

    const scheduleRefresh = (kc: Keycloak) => {
      const exp = (kc.tokenParsed?.exp ?? 0) * 1000;
      const in50s = 50_000;
      const minWait = 10_000;
      const wait = Math.max(minWait, exp - Date.now() - in50s);

      refreshTimer.current = window.setTimeout(async () => {
        try {
          const refreshed = await kc.updateToken(60);
          if (refreshed) updateAxiosToken(kc.token);
          if (kc.authenticated) scheduleRefresh(kc);
        } catch (error) {
          if (kc.authenticated) {
            Sentry.captureException(error);
            updateAxiosToken(undefined);
            navigateRef.current(ROUTE_PATHS.SESSION_EXPIRED);
          }
        }
      }, wait);
    };

    if (mock) {
      updateAxiosToken('mock-token');
      return clearTimer;
    }

    const kc = new Keycloak({
      url: KEYCLOAK_URL,
      realm: APP_SETTINGS.keycloakRealm,
      clientId: KEYCLOAK_CLIENT_ID,
    });

    kc.onAuthSuccess = () => {
      setTick(t => t + 1);
      updateAxiosToken(kc.token);
    };

    kc.onAuthRefreshSuccess = () => {
      setTick(t => t + 1);
      updateAxiosToken(kc.token);
    };

    kc.onAuthLogout = () => {
      clearUser();
      setTick(t => t + 1);
      updateAxiosToken(undefined);
    };

    kc.onTokenExpired = () => {
      kc.updateToken(5).catch(() => {
        clearUser();
        kc.logout();
      });
    };

    kc.init({
      // Avoid issues with third-party cookies between the browser and iframe hosted by Keycloak
      checkLoginIframe: false,
      onLoad: 'check-sso',
      pkceMethod: 'S256',
      silentCheckSsoRedirectUri:
        process.env.NODE_ENV === 'development' ? undefined : `${window.location.origin}/silent-check-sso.html`,
    })
      .then(() => {
        setKeycloak(kc);
        updateAxiosToken(kc.token);
        if (kc.authenticated) {
          scheduleRefresh(kc);
        }
      })
      .catch(error => {
        Sentry.captureException(error);
        updateAxiosToken(undefined);
        navigateRef.current(ROUTE_PATHS.ERROR, {
          replace: true,
          state: { errorCode: 503 },
        });
      })
      .finally(() => {
        setInitialized(true);
      });

    return clearTimer;
  }, [mock]);

  if (!initialized) {
    return <LoadingIndicator />;
  }

  return <KeycloakCtx.Provider value={{ keycloak, initialized }}>{children}</KeycloakCtx.Provider>;
};
